// Imports from external sources
// React, i18next, Router, Mantine, Further Stuff
import { 
    createSelector, 
    createSlice, 
    PayloadAction 
} from "@reduxjs/toolkit"

import {
    jwtDecode 
} from "jwt-decode"

import Cookies from "universal-cookie"


// Imports from this projects
// Pages, Components, Features, Hooks
import { 
    clientId 
} from "../../app/Consts"



//Interfaces
export interface AuthState {
    isAuthenticated: boolean
    tokens?: any
    userInfo?: {
        azp: string
        exp: number
        prefered_username: string
        family_name: string
        given_name: string
        name: string
        resource_access: {
            [id: string]: {
                roles: string[]
            }
        }
    }
}


interface AuthSliceState extends AuthState {
    // ONLY FOR DEBUGGING
    // if this is set the user will be assumed to have the following roles
    debugRoles: string[];
}
 


//Consts

/**
 * Extracts the infomration of an auth Token and creates an AuthState
 * @param tokens Keycloak tokens
 */
export const getAuthStateFromToken = (
  tokens: { idToken: string, refreshToken: string, token: string } | null = {
    idToken: "",
    refreshToken: "",
    token: ""
  }
): AuthState => {
  const decodedJwt =
    tokens && tokens.idToken ? (jwtDecode(tokens.idToken) as any) : {};
  const authState = {
    isAuthenticated: !!(tokens && tokens.token),
    tokens,
    userInfo: {
      resource_access: {},
      azp: "",
      exp: 0,
      preferred_username: "",
      family_name: "",
      given_name: "",
      name: "",
      ...decodedJwt
    }
  }

  return authState;
}


const cookies = new Cookies();

const cookieTokens = {
  idToken: cookies.get("idToken",),
  token: cookies.get("token"),
  refreshToken: "",
}

const initialState: AuthSliceState = getAuthStateFromToken((!!cookieTokens.idToken && !!cookieTokens.token) ? cookieTokens : null) as AuthSliceState;
initialState.debugRoles = [];

const auth = createSlice({
    name: "auth",
    initialState,
    reducers: {
        setAuthState(state, { payload }: PayloadAction<AuthState>) {
            const cookieOptions = { path: "", secure: true, sameSite: true, maxAge: 3600 };
            cookies.set("idToken", payload.tokens.idToken, cookieOptions);
            cookies.set("token", payload.tokens.token, cookieOptions);
            return {
                ...state,
                ...payload,
            };
        },
        setDebugRoles(state, { payload }: PayloadAction<string[]>) {
            return {
                ...state,
                debugRoles: payload.reduce((res, role) => {
                if (res.indexOf(role) < 0) {
                    res.push(role);
                }
                return res;
                }, [] as string[]),
            };
        },
    },
});


export const { 
    setAuthState,
    setDebugRoles 
} = auth.actions

export default auth.reducer


// selectors
type AuthSliceRoot = {
    auth: ReturnType<typeof auth.reducer>
}

export const selectAuth = (state: AuthSliceRoot) => state.auth;
export const selectTokens = (state: AuthSliceRoot) => state.auth.tokens
export const selectUserInfo = (state: AuthSliceRoot) => state.auth.userInfo
export const selectIsAuthenticated = (state: AuthSliceRoot) => state.auth.isAuthenticated

export const selectAccessToken = createSelector([selectTokens], (tokens) => {
    if (tokens) {
        return tokens.token
    }
    return undefined
})


export const selectUserRoles = createSelector([selectUserInfo], (userInfo) => {
    if (
        userInfo &&
        userInfo.resource_access &&
        userInfo.resource_access[clientId]
    ) {
        return userInfo.resource_access[clientId].roles
    }
    return [] as string[]
})


// Workaround to get these fields until supported by auth library
export const selectSubAndEmail = createSelector([selectTokens], (tokens) => {
    if (tokens === undefined) 
        return { sub: "", email: "" }
    const decodedJwt = jwtDecode(tokens.idToken)
    const subAndEmailInfo = Object.assign({ sub: "", email: "" }, decodedJwt)
    return subAndEmailInfo
})

// Workaround to get these fields until supported by auth library
export const selectSub = createSelector([selectTokens], (tokens) => {
    if (tokens === undefined) 
        return { sub: ""}
    const decodedJwt = jwtDecode(tokens.idToken)
    const subInfo = Object.assign({ sub: ""}, decodedJwt)
    return subInfo
})